import { computed, reactive, toValue } from "vue";
import { useSubscriptions } from "@/api/subscriptions.store";
import { useOrganizations } from "@manager/organizations/organizations.store";
import { usePlans } from "@manager/plans/plans.store";
import { find } from "lodash-es";
import { formatDateString } from "@/i18n";

import type { MaybeRefOrGetter } from "vue";
import type { Subscription } from "@/api/subscriptions.store";

export function useSubscription(subscriptionId: MaybeRefOrGetter<number>) {
  const { organizations } = useOrganizations({});
  const { subscriptions } = useSubscriptions();
  const { plans } = usePlans();

  const subscription = computed((): Subscription | null => {
    const id = toValue(subscriptionId);
    return subscriptions[id] ?? null;
  });

  const formattedRenewalDate = computed(() => {
    if (!subscription.value) return "";
    if (!subscription.value.renewalDate) return "";
    return formatDateString(subscription.value.renewalDate);
  });
  const formattedValidUntilDate = computed(() => {
    if (!subscription.value) return "";
    if (!subscription.value.validUntil) return "";
    return formatDateString(subscription.value.validUntil);
  });
  const formattedExpiredAtDate = computed(() => {
    if (!subscription.value) return "";
    if (!subscription.value.expiredAt) return "";
    return formatDateString(subscription.value.expiredAt);
  });

  const closeToExpiration = computed(() => {
    if (!subscription.value) return false;
    if (!subscription.value.validUntil) return false;
    const now = new Date();
    const validUntil = new Date(subscription.value.validUntil);
    const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
    return validUntil.getTime() - now.getTime() < THIRTY_DAYS;
  });
  const expired = computed(() => {
    if (!subscription.value) return false;
    if (subscription.value.expired) return true;
    if (subscription.value.expiredAt) return true;
    if (subscription.value.renewAutomatically) return false;
    if (!subscription.value.validUntil) return false;
    const validDate = new Date(subscription.value.validUntil);
    const now = new Date();
    return validDate.getTime() < now.getTime();
  });

  const organization = computed(() => {
    if (subscription.value?.plan !== "organization") return null;
    return find(organizations, (org) => org.subscriptionId == toValue(subscriptionId));
  });

  const plan = computed(() => {
    if (subscription.value?.plan == null) return null;
    return plans?.[subscription.value.plan];
  });

  return reactive({
    subscription,
    organization,
    plan,
    expired,
    closeToExpiration,
    formattedRenewalDate,
    formattedValidUntilDate,
    formattedExpiredAtDate,
  });
}
